export type ClipboardTable = string[][]

export function parseTabSeparatedTable(text: string): ClipboardTable | null {
    const normalized = text.replace(/\r\n?/g, '\n').replace(/\n$/, '')
    if (!normalized.includes('\t') || !normalized.includes('\n')) return null

    const rows: string[][] = [[]]
    let cell = ''
    let quoted = false

    for (let index = 0; index < normalized.length; index++) {
        const char = normalized[index]
        if (char === '"') {
            if (quoted && normalized[index + 1] === '"') {
                cell += '"'
                index++
            } else {
                quoted = !quoted
            }
        } else if (!quoted && (char === '\t' || char === '\n')) {
            rows[rows.length - 1].push(cell)
            cell = ''
            if (char === '\n') rows.push([])
        } else {
            cell += char
        }
    }
    rows[rows.length - 1].push(cell)

    if (rows.length < 2 || rows.reduce((count, row) => Math.max(count, row.length), 0) < 2) return null
    return rows
}

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char] || char))

export function tabSeparatedTableToHtml(text: string) {
    const rows = parseTabSeparatedTable(text)
    if (!rows) return null

    return `<table><tbody>${rows.map((row) => (
        `<tr>${row.map((cell) => `<td><p>${escapeHtml(cell)}</p></td>`).join('')}</tr>`
    )).join('')}</tbody></table>`
}

export function htmlTableToRows(html: string): ClipboardTable | null {
    if (!/<table[\s>]/i.test(html) || typeof DOMParser === 'undefined') return null
    const table = new DOMParser().parseFromString(html, 'text/html').querySelector('table')
    if (!table) return null

    const rows: string[][] = []
    Array.from(table.rows).forEach((row, rowIndex) => {
        rows[rowIndex] ||= []
        let columnIndex = 0
        Array.from(row.cells).forEach((cell) => {
            while (rows[rowIndex][columnIndex] !== undefined) columnIndex++
            const value = (cell.textContent || '').replace(/\s+/g, ' ').trim()
            const rowSpan = Math.max(1, cell.rowSpan)
            const colSpan = Math.max(1, cell.colSpan)
            for (let rowOffset = 0; rowOffset < rowSpan; rowOffset++) {
                rows[rowIndex + rowOffset] ||= []
                for (let columnOffset = 0; columnOffset < colSpan; columnOffset++) {
                    rows[rowIndex + rowOffset][columnIndex + columnOffset] = value
                }
            }
            columnIndex += colSpan
        })
    })
    return rows.length ? rows : null
}

export function tableToMarkdown(rows: ClipboardTable) {
    const columnCount = rows.reduce((count, row) => Math.max(count, row.length), 0)
    const formatRow = (row: string[]) => `| ${Array.from({ length: columnCount }, (_, index) => (
        (row[index] || '').replace(/\|/g, '\\|').replace(/\n/g, ' ')
    )).join(' | ')} |`
    return [formatRow(rows[0]), formatRow(Array(columnCount).fill('---')), ...rows.slice(1).map(formatRow)].join('\n')
}

export function clipboardTableToMarkdown(html: string, plainText: string) {
    const rows = htmlTableToRows(html) || parseTabSeparatedTable(plainText)
    return rows ? tableToMarkdown(rows) : null
}
